/**
 * Vite inlines import.meta.env at build time. Static deploys also load window.__ENV__
 * from /env-config.js (must load in <head> before the app bundle — see vite.config.js).
 */
import {
  DEFAULT_DEV_API_BASE_URL,
  DEFAULT_PROD_API_BASE_URL,
  resolveApiBaseUrl,
  shouldUseViteApiProxy,
} from "./apiBaseUrl";

function readRuntimeEnv() {
  if (typeof window === "undefined") return {};
  return window.__ENV__ && typeof window.__ENV__ === "object"
    ? window.__ENV__
    : {};
}

/** Strip broken quotes and fix common hosted misconfigurations. */
export function normalizeEnvValue(value) {
  let v = String(value ?? "").trim();
  if (!v) return "";

  v = v.replace(/^["']+/, "").replace(/["']+$/, "");

  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("//")) return `https:${v}`;
  if (v.includes(".jobscribe.in")) return `https://${v.replace(/^\/+/, "")}`;
  return v;
}

export function getViteEnv(key) {
  const runtime = readRuntimeEnv()[key];
  if (runtime != null && String(runtime).trim() !== "") {
    return normalizeEnvValue(runtime);
  }

  const built = import.meta.env[key];
  if (built != null && String(built).trim() !== "") {
    return normalizeEnvValue(built);
  }

  return "";
}

export { DEFAULT_DEV_API_BASE_URL, DEFAULT_PROD_API_BASE_URL };

/** Admin API base (includes `/api` suffix). Used by careers and shared API client. */
export function getApiBaseUrl() {
  return resolveApiBaseUrl(getViteEnv("VITE_API_BASE_URL"), {
    useViteProxy: shouldUseViteApiProxy(),
  });
}

/**
 * Franchise-requests API base (includes `/api` suffix). This endpoint lives on a
 * different host than the admin API, so it has its own env var
 * (VITE_FRANCHISE_API_BASE_URL). Falls back to the admin API base when unset so
 * the app never breaks if the variable is missing.
 */
export function getFranchiseApiBaseUrl() {
  const configured = getViteEnv("VITE_FRANCHISE_API_BASE_URL");
  if (configured) return configured.replace(/\/$/, "");
  return getApiBaseUrl();
}

/** Default Lilac Product Blogs API base */
export const DEFAULT_PRODUCT_BLOGS_BASE_URL =
  "https://neo.lilacinfotech.com/api/v1/product-blogs";

/**
 * Lilac Product Blogs API base URL. Separate host from the admin API.
 * Falls back to the documented neo.lilacinfotech.com endpoint when unset.
 */
export function getProductBlogsBaseUrl() {
  const configured = getViteEnv("VITE_PRODUCT_BLOGS_BASE_URL");
  if (configured) return configured.replace(/\/$/, "");
  return DEFAULT_PRODUCT_BLOGS_BASE_URL;
}

/** Lilac Product Blogs API key (`X-API-KEY` header). */
export function getProductBlogsApiKey() {
  return getViteEnv("VITE_PRODUCT_BLOGS_API_KEY");
}
